import * as React from 'react';
import { useContext } from 'react';
import TableComponent, { KeyHeader } from '../../shared/TableComponent';
import { DateUtil } from '../../../utils';
import { ProgressBar } from '../../shared/ProgressBar';
import { DocumentResults } from 'src/types/Document';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePollVertical } from '@fortawesome/free-solid-svg-icons';
import { appActions } from '../../../store';
import { ModalType } from '../../../types/Modal';
import { AppAgentContext } from '../../../context/ContextProvider';
import { useTranslate } from 'sbx-crm-menu-ui';
import { useQuery } from '@tanstack/react-query';
import { resultsDocumentService } from '../../../services/DocumentService';
import { useParams } from 'react-router-dom';

const classNameHeader = 'px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider';

type Props = { source: string }

const DocumentHistoryComponent = ({ source }: Props) => {
    const { id } = useParams();
    const { data, isLoading } = useQuery({
        queryKey: ['history'],
        queryFn: async () => await resultsDocumentService(parseInt(id as string)).then(res => {
            if (res.items) {
                return {
                    ...res,
                    items: (res.items).sort((a, b) => {
                        const date1 = new Date(a.updated_at);
                        const date2 = new Date(b.updated_at);
                        return date2.getTime() - date1.getTime();
                    }),
                };
            }
            return res;
        }),
        enabled: !!id && id !== 'create',
    });

    const { t: common } = useTranslate('common');
    const { t: agent } = useTranslate('agent');
    const { dispatch: dispatchApp } = useContext(AppAgentContext);
    return (
        <div>
            <TableComponent
                searchClassName={'pl-10 pr-4 py-2 !border-purple-300 !focus:border-purple-500 focus:ring focus:!ring-purple-200 focus:ring-opacity-50 rounded-full'}
                items={data?.items ?? []}
                loading={isLoading}
                tableClassName={'w-full'}
                rowClassName={'border-purple-100 hover:bg-purple-50 transition-colors duration-200'}
                pagePaginationClassName={(page, num) => {
                    return `w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                        page === num
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold'
                            : 'text-gray-600 hover:bg-purple-100'
                    }`;
                }}
                pagination
                search
                buttonPaginationClassName={'!bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full p-3 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-50'}
                header={[
                    {
                        type: KeyHeader.KEY,
                        label: 'Id',
                        key: 'id',
                        className: classNameHeader,
                    },
                    {
                        type: KeyHeader.VALUE,
                        label: common('date'),
                        className: classNameHeader,
                        toValue: (item) => {

                            if (item.created_at) {
                                const utcDate = !item.created_at.includes('Z') ? item.created_at + 'Z' : item.created_at;
                                return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate();
                            }
                            return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate();
                        },
                    },
                    {
                        type: KeyHeader.KEY,
                        label: agent('agent_id'),
                        key: 'sbx_ai_agent',
                        className: classNameHeader,
                    },
                    {
                        type: KeyHeader.VALUE,
                        label: agent('feelings'),
                        className: classNameHeader,
                        toValue: (item) => {
                            const positive = item.getResults()?.sentiment_analysis_resume?.positive?.toFixed(5) || '';
                            const neutral = item.getResults()?.sentiment_analysis_resume?.neutral?.toFixed(5) || '';
                            const negative = item.getResults()?.sentiment_analysis_resume?.negative?.toFixed(5) || '';

                            return (
                                <div className="w-[250px]">
                                    <ProgressBar data={[
                                        {
                                            value: positive,
                                            color: 'rgb(10, 222, 128)',
                                        },
                                        {
                                            value: neutral,
                                            color: 'rgb(251, 146, 60)',
                                        },
                                        {
                                            value: negative,
                                            color: 'rgb(248, 113, 113)',
                                        },
                                    ]} />
                                </div>
                            );
                        },
                    },
                    {
                        type: KeyHeader.VALUE,
                        label: common('status'),
                        className: classNameHeader,
                        toValue: (item) => item.getResults()?.status || '',
                    },
                ]}

                actions={[
                    {
                        label: common('see_results'),
                        icon: <FontAwesomeIcon icon={faSquarePollVertical} className="text-[18px]" />,
                        className: 'btn btn-sm',
                        onClick: (item) => {
                            dispatchApp(appActions.openModal({
                                type: ModalType.DOCUMENT_RESULTS,
                                data: {
                                    title: common('results'),
                                    onClose: () => dispatchApp(appActions.closeModal(ModalType.DOCUMENT_RESULTS)),
                                    data: { ...item.getResults(), source } as DocumentResults,
                                },
                            }));
                        },
                    },
                ]}
                actionsLabelClassName={`px-6 py-3  text-xs font-semibold text-purple-700 uppercase tracking-wider`}

            />
        </div>
    );
};

export default DocumentHistoryComponent;